import React, { useState, useEffect } from 'react';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Plus, Pencil, Trash2, Save, X, Loader2 } from 'lucide-react';
import { apiFetchJson } from './my/api';

const emptyArticle = {
  title_fr: '', title_en: '', excerpt_fr: '', excerpt_en: '',
  content_fr: '', content_en: '', category: 'serveurs', slug: '',
  image_url: '', published: true,
};

const categories = ['serveurs', 'cyber', 'ia', 'telephonie', 'impression', 'formation'];

export default function AdminNews() {
  const { lang } = useLang();
  const a = translations.admin;
  const n = translations.news;
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchArticles(); }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      setArticles(await apiFetchJson('/api/admin/news', {}, 'Erreur de chargement'));
    } catch { setArticles([]); }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleSave = async () => {
    setSaving(true);
    const data = { ...form };
    if (!data.slug) data.slug = generateSlug(data.title_fr);
    try {
      if (editing === 'new') {
        await apiFetchJson('/api/admin/news', {
          method: 'POST',
          body: JSON.stringify(data),
        }, 'Erreur de création');
      } else {
        await apiFetchJson(`/api/admin/news/${editing}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }, 'Erreur de mise à jour');
      }
      setEditing(null);
      setForm(emptyArticle);
      fetchArticles();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t(a.confirm_delete, lang))) return;
    try {
      await apiFetchJson(`/api/admin/news/${id}`, { method: 'DELETE' }, 'Erreur de suppression');
      fetchArticles();
    } catch {}
  };

  const startEdit = (article) => {
    setEditing(article.id);
    setForm({
      title_fr: article.title_fr, title_en: article.title_en,
      excerpt_fr: article.excerpt_fr, excerpt_en: article.excerpt_en,
      content_fr: article.content_fr, content_en: article.content_en,
      category: article.category, slug: article.slug,
      image_url: article.image_url || '', published: article.published,
    });
  };

  return (
    <main data-testid="admin-news-page" className="pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <h1 className="text-3xl font-bold text-white">{t(a.title, lang)}</h1>
          {!editing && (
            <button
              onClick={() => { setEditing('new'); setForm(emptyArticle); }}
              data-testid="admin-create-button"
              className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-lg transition-all hover:scale-105"
            >
              <Plus className="w-4 h-4" /> {t(a.create, lang)}
            </button>
          )}
        </div>

        {/* Editor */}
        {editing && (
          <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 mb-8" data-testid="article-editor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <Field label="Titre FR" name="title_fr" value={form.title_fr} onChange={handleChange} />
              <Field label="Title EN" name="title_en" value={form.title_en} onChange={handleChange} />
              <Field label="Chapeau FR" name="excerpt_fr" value={form.excerpt_fr} onChange={handleChange} />
              <Field label="Excerpt EN" name="excerpt_en" value={form.excerpt_en} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <TextArea label="Contenu FR" name="content_fr" value={form.content_fr} onChange={handleChange} />
              <TextArea label="Content EN" name="content_en" value={form.content_en} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Categorie</label>
                <select
                  name="category" value={form.category} onChange={handleChange}
                  data-testid="article-category-select"
                  className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-3 text-white outline-none"
                >
                  {categories.map(c => (
                    <option key={c} value={c}>{n.categories[c] ? t(n.categories[c], lang) : c}</option>
                  ))}
                </select>
              </div>
              <Field label="Slug" name="slug" value={form.slug} onChange={handleChange} placeholder="auto-generated" />
              <Field label="Image URL" name="image_url" value={form.image_url} onChange={handleChange} />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" name="published" checked={form.published} onChange={handleChange} className="rounded" data-testid="article-published-checkbox" />
                {tx(lang, 'Publie', 'Published', { es: 'Publish', de: 'Publizieren', it: 'Pubblicazione', pt: 'Publicar' })}
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving || !form.title_fr}
                data-testid="article-save-button"
                className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-lg transition-all disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t(a.save, lang)}
              </button>
              <button
                onClick={() => { setEditing(null); setForm(emptyArticle); }}
                data-testid="article-cancel-button"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 font-medium py-2.5 px-5 rounded-lg transition-all"
              >
                <X className="w-4 h-4" /> {t(a.cancel, lang)}
              </button>
            </div>
          </div>
        )}

        {/* Articles List */}
        {loading ? (
          <p className="text-slate-400 text-center py-10">{t(translations.common.loading, lang)}</p>
        ) : (
          <div className="space-y-3" data-testid="admin-articles-list">
            {articles.map((article) => (
              <div
                key={article.id}
                className="bg-[#15171E]/60 border border-white/5 rounded-xl p-5 flex items-center justify-between gap-4"
                data-testid={`admin-article-${article.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${article.published ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                    <span className="text-xs text-slate-500">{article.category}</span>
                  </div>
                  <h4 className="text-sm font-medium text-white truncate">
                    {lang === 'fr' ? article.title_fr : article.title_en}
                  </h4>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => startEdit(article)}
                    data-testid={`edit-article-${article.id}`}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition-all"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    data-testid={`delete-article-${article.id}`}
                    className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function Field({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
      <input
        type="text" name={name} value={value} onChange={onChange} placeholder={placeholder}
        data-testid={`article-${name}-input`}
        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-3 text-white placeholder-slate-500 outline-none text-sm"
      />
    </div>
  );
}

function TextArea({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
      <textarea
        name={name} value={value} onChange={onChange} rows={6}
        data-testid={`article-${name}-textarea`}
        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-3 text-white placeholder-slate-500 outline-none resize-none text-sm"
      />
    </div>
  );
}
